import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useGame } from "../hooks/useGameContext";
import { clearSession } from "../lib/storage";
import {
  clearJoinFormDrafts,
  getJoinCodeDraft,
  getJoinPasswordDraft,
  saveJoinCodeDraft,
  saveJoinPasswordDraft,
} from "../lib/formDrafts";

const SERVER_TIMEOUT_MS = 15_000;

type JoinMode = "player" | "spectator";

export function JoinRoomPage() {
  const { nickname, setNickname, emitUserAction, connected, room, playerId, actionError, clearActionError } = useGame();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const inviteCode = params.get("code")?.toUpperCase() ?? "";
  const pendingJoinRef = useRef(false);
  const [code, setCodeState] = useState(() => inviteCode || getJoinCodeDraft());
  const [password, setPasswordState] = useState(() => getJoinPasswordDraft());
  const [joinMode, setJoinMode] = useState<JoinMode>("player");
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasValidNickname = nickname.trim().length >= 2;
  const fromInvite = inviteCode.length === 6;

  const setCode = (value: string) => {
    const next = value.toUpperCase();
    setCodeState(next);
    if (!fromInvite) saveJoinCodeDraft(next);
  };

  const setPassword = (value: string) => {
    setPasswordState(value);
    saveJoinPasswordDraft(value);
  };

  useEffect(() => {
    if (inviteCode) setCode(inviteCode);
  }, [inviteCode]);

  useEffect(() => {
    if (room && playerId && pendingJoinRef.current) {
      pendingJoinRef.current = false;
      setJoining(false);
      setError(null);
      clearJoinFormDrafts();
      const me = room.players.find((p) => p.id === playerId);
      const inGame = room.game && room.game.phase !== "LOBBY";
      if (inGame || me?.role === "spectator") {
        navigate("/game");
      } else {
        navigate("/lobby");
      }
    }
  }, [room, playerId, navigate]);

  useEffect(() => {
    if (!joining) return;
    const timeout = window.setTimeout(() => {
      if (!room) {
        setJoining(false);
        setError("Le serveur met du temps à répondre. Vos informations sont conservées — réessayez dans un instant.");
      }
    }, SERVER_TIMEOUT_MS);
    return () => window.clearTimeout(timeout);
  }, [joining, room]);

  useEffect(() => {
    if (actionError && (joining || pendingJoinRef.current)) {
      setJoining(false);
      pendingJoinRef.current = false;
      setError(actionError);
      clearActionError();
    }
  }, [actionError, joining, clearActionError]);

  const handleJoin = () => {
    if (!hasValidNickname) {
      setError("Choisissez un pseudonyme (2 caractères minimum) pour rejoindre le salon.");
      return;
    }
    if (!connected) {
      setError("En attente de connexion au serveur…");
      return;
    }
    if (code.length !== 6) {
      setError("Code de salon invalide.");
      return;
    }
    setError(null);
    clearActionError();
    clearSession();
    pendingJoinRef.current = true;
    setJoining(true);
    const sent = emitUserAction("room:join", {
      code: code.toUpperCase(),
      nickname: nickname.trim(),
      password: password || undefined,
      asSpectator: joinMode === "spectator",
    });
    if (!sent) {
      pendingJoinRef.current = false;
      setJoining(false);
      setError("Impossible de contacter le serveur. Vos informations sont conservées.");
    }
  };

  return (
    <div className="max-w-md mx-auto cw-panel p-8 space-y-6">
      <h1 className="cw-title text-3xl font-bold">Rejoindre</h1>

      {fromInvite && (
        <p className="text-sm text-violet-300/80">
          Invitation au salon <strong className="text-orange-400 tracking-widest">{inviteCode}</strong>
        </p>
      )}

      {!connected && <p className="text-sm text-yellow-400">Connexion au serveur en cours…</p>}
      {error && <p className="text-sm text-red-400" role="alert">{error}</p>}

      <label className="block">
        <span className="text-sm text-violet-300">Votre pseudonyme</span>
        <input
          className="cw-input mt-1"
          value={nickname}
          onChange={(e) => setNickname(e.target.value.trim())}
          placeholder="Ex : DJNova"
          minLength={2}
          maxLength={24}
          autoFocus={fromInvite && !hasValidNickname}
          aria-label="Pseudonyme"
          aria-required
        />
        {!hasValidNickname && (
          <p className="text-xs text-orange-300/90 mt-1">
            Entrez votre pseudo pour entrer dans le salon.
          </p>
        )}
      </label>

      {!fromInvite && (
        <label className="block">
          <span className="text-sm">Code du salon</span>
          <input
            className="cw-input mt-1 text-center text-2xl tracking-widest uppercase"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            aria-label="Code salon"
          />
        </label>
      )}

      {fromInvite && (
        <p className="text-center text-2xl tracking-[0.3em] text-orange-400 font-bold" aria-label="Code du salon">
          {code}
        </p>
      )}

      <fieldset className="space-y-2">
        <legend className="text-sm text-violet-300">Mode de participation</legend>
        <label className="flex items-center gap-2">
          <input
            type="radio"
            name="join-mode"
            checked={joinMode === "player"}
            onChange={() => setJoinMode("player")}
          />
          <span>Joueur — frise chronologique et placements</span>
        </label>
        <label className="flex items-center gap-2">
          <input
            type="radio"
            name="join-mode"
            checked={joinMode === "spectator"}
            onChange={() => setJoinMode("spectator")}
          />
          <span>Spectateur — écoute uniquement, sans frise</span>
        </label>
        <p className="text-xs text-violet-400/60">
          En spectateur, vous entendez les extraits mais ne placez pas de cartes. Utile si la partie a déjà commencé.
        </p>
      </fieldset>

      <label className="block">
        <span className="text-sm">Mot de passe (si requis)</span>
        <input
          type="password"
          className="cw-input mt-1"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </label>

      <button
        type="button"
        className="cw-btn cw-btn-primary w-full"
        disabled={code.length !== 6 || joining || !connected || !hasValidNickname}
        onClick={handleJoin}
      >
        {joining ? "Connexion…" : joinMode === "spectator" ? "Regarder en spectateur" : "Entrer dans le salon"}
      </button>

      <p className="text-center text-sm">
        <Link to="/" className="text-violet-400 underline hover:text-violet-200">
          Retour à l&apos;accueil
        </Link>
      </p>
    </div>
  );
}
